import React, { Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch, markClientAuthRevoked } from './api';
import { AuthProvider, useAuth } from './auth';
import { ToastProvider } from './toast';
import { DialogProvider } from './dialog';
import { Modal } from './components/Modal';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { AgentsPage } from './pages/AgentsPage';
import { ProfilePage } from './pages/ProfilePage';
import { NotFoundPage } from './pages/NotFoundPage';

const TarotToolPage = React.lazy(() => import('./pages/TarotToolPage').then((m) => ({ default: m.TarotToolPage })));
const ConstellationToolPage = React.lazy(() => import('./pages/ConstellationToolPage').then((m) => ({ default: m.ConstellationToolPage })));
const LiuyaoToolPage = React.lazy(() => import('./pages/LiuyaoToolPage').then((m) => ({ default: m.LiuyaoToolPage })));
const MeihuaToolPage = React.lazy(() => import('./pages/MeihuaToolPage').then((m) => ({ default: m.MeihuaToolPage })));
const QimenToolPage = React.lazy(() => import('./pages/QimenToolPage').then((m) => ({ default: m.QimenToolPage })));
const ZiweiToolPage = React.lazy(() => import('./pages/ZiweiToolPage').then((m) => ({ default: m.ZiweiToolPage })));
const BaziToolPage = React.lazy(() => import('./pages/BaziToolPage').then((m) => ({ default: m.BaziToolPage })));
const WheelToolPage = React.lazy(() => import('./pages/WheelToolPage').then((m) => ({ default: m.WheelToolPage })));
const NicknameToolPage = React.lazy(() => import('./pages/NicknameToolPage').then((m) => ({ default: m.NicknameToolPage })));
const MbtiToolPage = React.lazy(() => import('./pages/MbtiToolPage').then((m) => ({ default: m.MbtiToolPage })));
const ZodiacToolPage = React.lazy(() => import('./pages/ZodiacToolPage').then((m) => ({ default: m.ZodiacToolPage })));
const CatchTheCatToolPage = React.lazy(() => import('./pages/CatchTheCatToolPage').then((m) => ({ default: m.CatchTheCatToolPage })));
const AstrologyToolPage = React.lazy(() => import('./pages/AstrologyToolPage').then((m) => ({ default: m.AstrologyToolPage })));
const LifeRestartToolPage = React.lazy(() => import('./pages/LifeRestartToolPage').then((m) => ({ default: m.LifeRestartToolPage })));
const Game2048ToolPage = React.lazy(() => import('./pages/Game2048ToolPage').then((m) => ({ default: m.Game2048ToolPage })));
const Game2048LiteToolPage = React.lazy(() => import('./pages/Game2048LiteToolPage').then((m) => ({ default: m.Game2048LiteToolPage })));

function getRealtimeFailureHintThreshold() {
  const raw = Number(import.meta.env.VITE_REALTIME_WS_FAILURE_HINT_THRESHOLD || 3);
  if (!Number.isFinite(raw)) return 3;
  return Math.max(1, Math.floor(raw));
}

const REALTIME_WS_FAILURE_HINT_THRESHOLD = getRealtimeFailureHintThreshold();

function maskEmail(email: string) {
  const s = String(email || '').trim();
  const at = s.indexOf('@');
  if (at <= 0) return s ? '***' : '';
  const name = s.slice(0, at);
  const domain = s.slice(at + 1);
  if (/^\d+$/.test(name) && name.length >= 6) {
    const head = name.slice(0, Math.max(1, name.length - 6));
    const tail = name.slice(-2);
    return `${head}****${tail}@${domain}`;
  }
  if (name.length <= 4) return `${name[0] || '*'}****@${domain}`;
  return `${name.slice(0, 2)}****${name.slice(-2)}@${domain}`;
}

function formatOnlineDuration(totalSeconds: number) {
  const sec = Math.max(0, Math.floor(Number(totalSeconds || 0)));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}时${String(m).padStart(2, '0')}分`;
  if (m > 0) return `${m}分${String(s).padStart(2, '0')}秒`;
  return `${s}秒`;
}

function sanitizeMaintenanceHtml(input: string) {
  const raw = String(input || '');
  return raw
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+=(['"]).*?\1/gi, '')
    .replace(/\son\w+=([^\s>]+)/gi, '');
}

function Shell() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [opens, setOpens] = useState<number | null>(null);
  const [usersCount, setUsersCount] = useState<number | null>(null);
  const [icpText, setIcpText] = useState<string>('');
  const [icpUrl, setIcpUrl] = useState<string>('');
  const [mpsText, setMpsText] = useState<string>('');
  const [mpsUrl, setMpsUrl] = useState<string>('');
  const [onlineSeconds, setOnlineSeconds] = useState(0);
  const [lastActiveAt, setLastActiveAt] = useState<number | null>(null);
  const [realtimeOnlineCount, setRealtimeOnlineCount] = useState<number | null>(null);
  const [realtimeStatus, setRealtimeStatus] = useState<'loading' | 'online' | 'offline' | 'error'>('loading');
  const [realtimeHint, setRealtimeHint] = useState<string>('');
  const [maintenancePopup, setMaintenancePopup] = useState<{ html: string; text: string; ts: number } | null>(null);
  const onlineBaseSecondsRef = useRef(0);
  const onlineAccumulatedMsRef = useRef(0);
  const onlineLastTsRef = useRef(0);
  const onlineActiveRef = useRef(false);
  const onlineFlushLockRef = useRef(false);
  const forceLogoutRef = useRef(false);
  const [followSystem, setFollowSystem] = useState<boolean>(() => {
    const key = 'divination_theme_preference';
    const saved = String(localStorage.getItem(key) || '').trim();
    return saved !== 'light' && saved !== 'dark';
  });
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const key = 'divination_theme_preference';
    const saved = String(localStorage.getItem(key) || '').trim();
    if (saved === 'light' || saved === 'dark') return saved;
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    if (user?.id) forceLogoutRef.current = false;
  }, [user?.id]);

  useEffect(() => {
    const key = 'site_open_sent_v1';
    const sent = sessionStorage.getItem(key) === '1';
    let cancelled = false;
    const run = async () => {
      for (let i = 0; i < 3; i++) {
        try {
          const res = sent
            ? await apiFetch<{ ok: boolean; opens: number; users: number }>('/api/metrics/summary', { method: 'GET' })
            : await apiFetch<{ ok: boolean; opens: number; users: number }>('/api/metrics/open', { method: 'POST', body: JSON.stringify({}) });
          if (cancelled) return;
          sessionStorage.setItem(key, '1');
          setOpens(res?.opens ?? null);
          setUsersCount(res?.users ?? null);
          return;
        } catch {
          if (i === 2) {
            if (cancelled) return;
            setOpens(null);
            setUsersCount(null);
            return;
          }
          await new Promise((r) => setTimeout(r, 600 + i * 600));
        }
      }
    };
    const id = window.setTimeout(run, 800);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setRealtimeOnlineCount(null);
      setRealtimeStatus('loading');
      setRealtimeHint('');
      return;
    }
    let ws: WebSocket | null = null;
    let stopped = false;
    let reconnectTimer: number | null = null;
    let heartbeatTimer: number | null = null;
    let initialConnecting = true;
    let consecutiveFailures = 0;
    const connect = () => {
      if (stopped) return;
      if (initialConnecting) setRealtimeStatus((prev) => (prev === 'online' ? 'online' : 'loading'));
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      ws = new WebSocket(`${protocol}://${window.location.host}/ws/presence`);
      ws.onopen = () => {
        if (stopped) return;
        initialConnecting = false;
        consecutiveFailures = 0;
        setRealtimeHint('');
        setRealtimeStatus('online');
        heartbeatTimer = window.setInterval(() => {
          try {
            ws?.send(JSON.stringify({ type: 'heartbeat', ts: Date.now() }));
          } catch {}
        }, 2500);
      };
      ws.onerror = () => {
        if (stopped) return;
        setRealtimeStatus('error');
      };
      ws.onmessage = (ev) => {
        let msg: any = null;
        try {
          msg = JSON.parse(String(ev.data || ''));
        } catch {
          return;
        }
        if (msg?.type === 'online_count' || msg?.type === 'presence_state') {
          const count = Number(msg?.onlineCount || 0);
          if (Number.isFinite(count)) setRealtimeOnlineCount(Math.max(0, Math.floor(count)));
          setRealtimeStatus('online');
          return;
        }
        if (msg?.type === 'maintenance') {
          const html = sanitizeMaintenanceHtml(String(msg?.html || ''));
          const text = String(msg?.text || '').trim();
          setMaintenancePopup({ html, text, ts: Number(msg?.ts || Date.now()) });
          return;
        }
        if (msg?.type === 'force_logout') {
          if (forceLogoutRef.current) return;
          forceLogoutRef.current = true;
          markClientAuthRevoked();
          try {
            localStorage.removeItem('had_session_v1');
          } catch {
          }
          logout()
            .catch(() => {})
            .finally(() => {
              navigate('/login?forced=1', { replace: true });
            });
        }
      };
      ws.onclose = () => {
        if (heartbeatTimer) {
          window.clearInterval(heartbeatTimer);
          heartbeatTimer = null;
        }
        if (stopped) return;
        consecutiveFailures += 1;
        if (consecutiveFailures >= REALTIME_WS_FAILURE_HINT_THRESHOLD) {
          setRealtimeHint('实时通道不可用，请检查反向代理 WebSocket 转发。');
        }
        setRealtimeStatus('offline');
        reconnectTimer = window.setTimeout(connect, 1800);
      };
    };
    connect();
    return () => {
      stopped = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      if (heartbeatTimer) window.clearInterval(heartbeatTimer);
      try {
        ws?.close();
      } catch {}
    };
  }, [logout, navigate, user]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      for (let i = 0; i < 3; i++) {
        try {
          const r = await apiFetch<{ ok: boolean; icpText?: string; icpUrl?: string; mpsText?: string; mpsUrl?: string }>('/api/public/site', { method: 'GET' });
          if (cancelled) return;
          setIcpText(String(r?.icpText || ''));
          setIcpUrl(String(r?.icpUrl || ''));
          setMpsText(String(r?.mpsText || ''));
          setMpsUrl(String(r?.mpsUrl || ''));
          return;
        } catch {
          if (i === 2) {
            if (cancelled) return;
            setIcpText('');
            setIcpUrl('');
            setMpsText('');
            setMpsUrl('');
            return;
          }
          await new Promise((r) => setTimeout(r, 600 + i * 600));
        }
      }
    };
    const id = window.setTimeout(run, 800);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, []);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    const bg = theme === 'dark' ? '#070a14' : '#ecfeff';
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.setAttribute('name', 'theme-color');
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.setAttribute('content', bg);
  }, [theme]);

  useEffect(() => {
    if (!followSystem) return;
    const mq = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [followSystem]);

  const links = useMemo(() => {
    return [
      { to: '/', label: '首页' },
      { to: '/agents', label: '馆内灵使', auth: true },
      { to: '/profile', label: '我的小馆', auth: true, hideWhenAuthed: true }
    ];
  }, []);

  useEffect(() => {
    if (!user) {
      setOnlineSeconds(0);
      setLastActiveAt(null);
      onlineBaseSecondsRef.current = 0;
      onlineAccumulatedMsRef.current = 0;
      onlineLastTsRef.current = 0;
      onlineActiveRef.current = false;
      return;
    }
    const base = Math.max(0, Math.floor(Number((user as any)?.onlineTotalSeconds || 0)));
    onlineBaseSecondsRef.current = base;
    onlineAccumulatedMsRef.current = 0;
    setOnlineSeconds(base);
    setLastActiveAt((user as any)?.lastActiveAt ? Number((user as any).lastActiveAt) : null);
    onlineLastTsRef.current = Date.now();
    onlineActiveRef.current = document.visibilityState === 'visible' && document.hasFocus();
  }, [user?.id, (user as any)?.onlineTotalSeconds, (user as any)?.lastActiveAt]);

  useEffect(() => {
    if (!user) return;
    let timer: number | null = null;
    const postHeartbeat = async (elapsedMs: number, useKeepalive = false) => {
      const safe = Math.max(0, Math.floor(elapsedMs));
      if (!safe && !useKeepalive) return;
      if (!useKeepalive && onlineFlushLockRef.current) return;
      if (!useKeepalive) onlineFlushLockRef.current = true;
      try {
        const res = await fetch('/api/auth/heartbeat', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ elapsedMs: safe }),
          keepalive: useKeepalive
        });
        if (!res.ok) throw new Error('HEARTBEAT_FAILED');
        const data = await res.json();
        const nextSeconds = Math.max(0, Math.floor(Number(data?.onlineTotalSeconds || 0)));
        onlineBaseSecondsRef.current = nextSeconds;
        onlineAccumulatedMsRef.current = 0;
        setOnlineSeconds(nextSeconds);
        setLastActiveAt(data?.lastActiveAt ? Number(data.lastActiveAt) : Date.now());
      } catch {
        if (!useKeepalive) onlineAccumulatedMsRef.current += safe;
      } finally {
        if (!useKeepalive) onlineFlushLockRef.current = false;
      }
    };

    const flush = async (useKeepalive = false) => {
      const elapsed = onlineAccumulatedMsRef.current;
      onlineAccumulatedMsRef.current = 0;
      await postHeartbeat(elapsed, useKeepalive);
      onlineLastTsRef.current = Date.now();
    };

    const onVisibility = () => {
      const now = Date.now();
      const wasActive = onlineActiveRef.current;
      if (wasActive) onlineAccumulatedMsRef.current += Math.max(0, now - onlineLastTsRef.current);
      onlineLastTsRef.current = now;
      onlineActiveRef.current = document.visibilityState === 'visible' && document.hasFocus();
      if (!onlineActiveRef.current) void flush(true);
    };

    const onFocus = () => {
      onlineLastTsRef.current = Date.now();
      onlineActiveRef.current = document.visibilityState === 'visible' && document.hasFocus();
    };

    const onBlur = () => {
      const now = Date.now();
      if (onlineActiveRef.current) onlineAccumulatedMsRef.current += Math.max(0, now - onlineLastTsRef.current);
      onlineActiveRef.current = false;
      onlineLastTsRef.current = now;
      void flush(true);
    };

    const onPageHide = () => {
      const now = Date.now();
      if (onlineActiveRef.current) onlineAccumulatedMsRef.current += Math.max(0, now - onlineLastTsRef.current);
      const elapsed = Math.max(0, Math.floor(onlineAccumulatedMsRef.current));
      onlineAccumulatedMsRef.current = 0;
      if (elapsed > 0) {
        try {
          const payload = new Blob([JSON.stringify({ elapsedMs: elapsed })], { type: 'application/json' });
          navigator.sendBeacon('/api/auth/heartbeat', payload);
        } catch {
        }
      }
    };

    timer = window.setInterval(() => {
      const now = Date.now();
      if (onlineActiveRef.current) onlineAccumulatedMsRef.current += Math.max(0, now - onlineLastTsRef.current);
      onlineLastTsRef.current = now;
      const displaySeconds = onlineBaseSecondsRef.current + Math.floor(onlineAccumulatedMsRef.current / 1000);
      setOnlineSeconds(displaySeconds);
    }, 1000);

    const sendTimer = window.setInterval(() => {
      void flush(false);
    }, 30000);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      if (timer != null) window.clearInterval(timer);
      window.clearInterval(sendTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('pagehide', onPageHide);
      void flush(true);
    };
  }, [user?.id]);

  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="container topbar-inner">
          <div className="brand">
            <div className="brand-title">云间小馆</div>
            <div className="brand-sub">
              <span>云间小馆 · 轻松一刻，灵感入云</span>
              <br />
              {opens != null
                ? usersCount != null
                  ? `叁拾版启幕至今，雅用累计 ${opens} 次，知音共 ${usersCount} 位`
                  : `叁拾版启幕至今，雅用累计 ${opens} 次`
                : '云间茶已温，欢迎归来'}
            </div>
          </div>
          <div className="nav">
            {links
              .filter((l) => !l.auth || !!user)
              .filter((l: any) => !(l.hideWhenAuthed && user))
              .map((l) => (
                <NavLink key={l.to} to={l.to} className={({ isActive }) => `pill ${isActive ? 'active' : ''}`}>
                  {l.label}
                </NavLink>
              ))}
            <button
              className="pill"
              type="button"
              onClick={() => {
                const key = 'divination_theme_preference';
                const next = theme === 'dark' ? 'light' : 'dark';
                setTheme(next);
                localStorage.setItem(key, next);
                setFollowSystem(false);
              }}
            >
              灯火：{theme === 'dark' ? '夜' : '昼'}
            </button>
            {loading ? (
              <span className="pill">候片刻…</span>
            ) : user ? (
              <>
                <span className="pill" title={lastActiveAt ? `最后活跃：${new Date(lastActiveAt).toLocaleString()}` : '最后活跃：-'}>在线：{formatOnlineDuration(onlineSeconds)}</span>
                <span className="pill" title={realtimeStatus === 'online' ? '实时在线连接正常' : realtimeStatus === 'loading' ? '实时在线连接中' : realtimeStatus === 'offline' ? '实时在线连接断开' : '实时在线连接异常'}>
                  实时在线人数：
                  {realtimeOnlineCount == null ? '—' : `${realtimeOnlineCount}人`}
                  {realtimeStatus === 'loading' ? '（加载中）' : realtimeStatus === 'offline' ? '（离线）' : realtimeStatus === 'error' ? '（异常）' : ''}
                  {realtimeHint ? ` · ${realtimeHint}` : ''}
                </span>
                <button
                  className="pill"
                  type="button"
                  onClick={() => {
                    navigate('/profile', { replace: false });
                  }}
                >
                  {String((user as any)?.nickname || '').trim() || '知音'}
                  {((user as any)?.email ? ` · ${maskEmail(String((user as any).email))}` : '')}
                </button>
              </>
            ) : (
              <NavLink to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} className="pill">
                入馆
              </NavLink>
            )}
          </div>
        </div>
      </div>

      <div className="app-main">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/ai" element={<RequireAuth element={<Navigate to="/profile?tab=ai" replace />} />} />
          <Route path="/agents" element={<RequireAuth element={<AgentsPage />} />} />
          <Route path="/profile/*" element={<RequireAuth element={<ProfilePage />} />} />
          <Route path="/shares" element={<RequireAuth element={<Navigate to="/profile?tab=shares" replace />} />} />
          <Route path="/tools/tarot" element={<RequireAuth element={<Lazy element={<TarotToolPage />} />} />} />
          <Route path="/tools/constellation" element={<RequireAuth element={<Lazy element={<ConstellationToolPage />} />} />} />
          <Route path="/tools/liuyao" element={<RequireAuth element={<Lazy element={<LiuyaoToolPage />} />} />} />
          <Route path="/tools/meihua" element={<RequireAuth element={<Lazy element={<MeihuaToolPage />} />} />} />
          <Route path="/tools/qimen" element={<RequireAuth element={<Lazy element={<QimenToolPage />} />} />} />
          <Route path="/tools/ziwei" element={<RequireAuth element={<Lazy element={<ZiweiToolPage />} />} />} />
          <Route path="/tools/bazi" element={<RequireAuth element={<Lazy element={<BaziToolPage />} />} />} />
          <Route path="/tools/wheel" element={<RequireAuth element={<Lazy element={<WheelToolPage />} />} />} />
          <Route path="/tools/nickname" element={<RequireAuth element={<Lazy element={<NicknameToolPage />} />} />} />
          <Route path="/tools/mbti" element={<RequireAuth element={<Lazy element={<MbtiToolPage />} />} />} />
          <Route path="/tools/zodiac" element={<RequireAuth element={<Lazy element={<ZodiacToolPage />} />} />} />
          <Route path="/tools/zodiac/compatibility" element={<RequireAuth element={<Lazy element={<ZodiacToolPage />} />} />} />
          <Route path="/tools/catchthecat" element={<RequireAuth element={<Lazy element={<CatchTheCatToolPage />} />} />} />
          <Route path="/tools/astrology" element={<RequireAuth element={<Lazy element={<AstrologyToolPage />} />} />} />
          <Route path="/tools/liferestart" element={<RequireAuth element={<Lazy element={<LifeRestartToolPage />} />} />} />
          <Route path="/tools/2048" element={<RequireAuth element={<Lazy element={<Game2048ToolPage />} />} />} />
          <Route path="/tools/2048-lite" element={<RequireAuth element={<Lazy element={<Game2048LiteToolPage />} />} />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>

      <div className="container footer">
        <div>© {new Date().getFullYear()} 云间小馆 · 叁拾版</div>
        <div className="footer-icp">
          {icpText || mpsText ? (
            <>
              {icpText ? (
                icpUrl ? (
                  <a href={icpUrl} target="_blank" rel="noreferrer">
                    {icpText}
                  </a>
                ) : (
                  icpText
                )
              ) : null}
              {icpText && mpsText ? <span style={{ opacity: 0.6, margin: '0 10px' }}>·</span> : null}
              {mpsText ? (
                <a href={mpsUrl || 'https://beian.mps.gov.cn/#/query/webSearch'} target="_blank" rel="noreferrer">
                  {mpsText}
                </a>
              ) : null}
            </>
          ) : (
            '\u00A0'
          )}
        </div>
      </div>
      <Modal open={Boolean(maintenancePopup)} title="系统维护通知" onClose={() => setMaintenancePopup(null)}>
        <div className="std-dialog">
          <div className="std-dialog-message">
            <div dangerouslySetInnerHTML={{ __html: maintenancePopup?.html || '' }} />
          </div>
          <div className="dialog-actions">
            <button className="btn" type="button" onClick={() => setMaintenancePopup(null)}>
              我知道了
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function RequireAuth({ element }: { element: React.ReactElement }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="container muted">加载中…</div>;
  if (!user) return <LoginPage forcedNext={location.pathname + location.search} />;
  return element;
}

function Lazy({ element }: { element: React.ReactElement }) {
  return (
    <Suspense fallback={<div className="container muted">加载中…</div>}>
      {element}
    </Suspense>
  );
}

export function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <DialogProvider>
          <Shell />
        </DialogProvider>
      </AuthProvider>
    </ToastProvider>
  );
}
